// src/components/Admin/PostManager/PostsLogTable.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, TextField, TableContainer, Paper, Table, TableHead,
    TableRow, TableCell, TableBody, CircularProgress, Alert,
    MenuItem, Select, Typography, Pagination, Button,
    Stack, Divider, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../../../api/axios';
import * as XLSX from 'xlsx';

const actionLabel = {
    soft_delete: '삭제',
    blind: '블라인드',
    unblind: '블라인드 해제',
    edit: '수정',
};

const ROWS_PER_PAGE = 15;

export default function PostsLogTable() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ✅ 모바일은 카드

    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);

    /* ───────── 데이터 로드 ───────── */
    useEffect(() => {
        api.get('/api/posts/logs/')
            .then((res) => setLogs(res.data.logs || []))
            .catch(() => setError('로그를 불러오는 데 실패했습니다.'))
            .finally(() => setLoading(false));
    }, []);

    /* ───────── 필터링 ───────── */
    const filtered = useMemo(() => (
        logs
            .filter((l) => (actionFilter === 'all' ? true : l.action === actionFilter))
            .filter((l) => {
                const q = search.toLowerCase();
                return (
                    (l.post_title || '').toLowerCase().includes(q) ||
                    (l.action || '').toLowerCase().includes(q) ||
                    (l.memo || '').toLowerCase().includes(q) ||
                    (l.acted_by || '').toLowerCase().includes(q)
                );
            })
    ), [logs, search, actionFilter]);

    useEffect(() => setPage(1), [search, actionFilter]);

    const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE) || 1;
    const paginated = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

    // ───────── 엑셀 내보내기 ─────────
    const handleExportExcel = () => {
        const rows = filtered.map((l) => ({
            ID: l.id,
            게시글ID: l.post_id,
            게시글제목: l.post_title,
            행동: actionLabel[l.action] || l.action,
            처리자: l.acted_by || '-',
            시간: l.acted_at ? new Date(l.acted_at).toLocaleString('ko-KR') : '',
            사유: l.memo || '',
        }));

        if (!rows.length) return alert('엑셀로 내보낼 로그가 없습니다.');

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'PostLogs');
        const today = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `post_logs_${today}.xlsx`);
    };

    /* ───────── 로딩 / 에러 ───────── */
    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) return <Alert severity="error">{error}</Alert>;

    /* ───────── 상단 검색/필터 바 ───────── */
    const HeaderBar = () => (
        <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={1.5}
            sx={{ mb: 2, alignItems: isMobile ? 'stretch' : 'center' }}
        >
            <TextField
                label="게시글/행동/사유/처리자"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth={isMobile}
                sx={{ minWidth: isMobile ? '100%' : 240 }}
            />

            <Select
                size="small"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                fullWidth={isMobile}
                sx={{ minWidth: isMobile ? '100%' : 160 }}
            >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="soft_delete">삭제</MenuItem>
                <MenuItem value="blind">블라인드</MenuItem>
                <MenuItem value="unblind">블라인드 해제</MenuItem>
            </Select>

            <Typography variant="body2" color="text.secondary">
                총 {logs.length}개 / 현재 {filtered.length}개
            </Typography>

            <Button
                variant="outlined"
                size="small"
                onClick={handleExportExcel}
                fullWidth={isMobile}
            >
                📥 엑셀로 내보내기
            </Button>
        </Stack>
    );

    /* ───────── 모바일: 카드 리스트 ───────── */
    const MobileList = () => (
        <Stack spacing={1.25}>
            {paginated.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    🔍 조건에 맞는 로그가 없습니다.
                </Paper>
            ) : (
                paginated.map((l) => {
                    const href = `/main/posts/${l.post_id}`;
                    const action = actionLabel[l.action] || l.action;
                    const actedAt = l.acted_at ? new Date(l.acted_at).toLocaleString('ko-KR') : '-';

                    return (
                        <Paper key={l.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                    LOG #{l.id}
                                </Typography>
                                <Typography variant="caption" fontWeight={900}>
                                    {action}
                                </Typography>
                            </Stack>

                            <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 800, wordBreak: 'break-word' }}>
                                {l.post_title || '-'}
                            </Typography>

                            <Stack spacing={0.25} sx={{ mt: 1 }}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">게시글</Typography>
                                    <Typography variant="caption" fontWeight={700}>#{l.post_id}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">처리자</Typography>
                                    <Typography variant="caption" fontWeight={700}>{l.acted_by || '-'}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">시간</Typography>
                                    <Typography variant="caption" fontWeight={700}>{actedAt}</Typography>
                                </Stack>
                            </Stack>

                            <Divider sx={{ my: 1 }} />

                            <Stack spacing={0.75}>
                                <Typography variant="caption" color="text.secondary">
                                    사유
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {l.memo || '-'}
                                </Typography>

                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}
                                >
                                    게시글 열기
                                </Button>
                            </Stack>
                        </Paper>
                    );
                })
            )}
        </Stack>
    );

    /* ───────── PC: 테이블 ───────── */
    const DesktopTable = () => (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
                borderRadius: 2,
                '& tr:hover': { bgcolor: 'grey.100' },
                '& td, & th': {
                    borderBottom: '1px solid #eee',
                    p: 1.25,
                    whiteSpace: 'nowrap',
                },
                '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
            }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell align="center">ID</TableCell>
                        <TableCell align="left">게시글</TableCell>
                        <TableCell align="center">행동</TableCell>
                        <TableCell align="center">처리자</TableCell>
                        <TableCell align="center">시간</TableCell>
                        <TableCell align="center">사유</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {paginated.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                🔍 조건에 맞는 로그가 없습니다.
                            </TableCell>
                        </TableRow>
                    ) : (
                        paginated.map((l) => (
                            <TableRow key={l.id}>
                                <TableCell align="center">{l.id}</TableCell>
                                <TableCell align="left">
                                    <a
                                        href={`/main/posts/${l.post_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none', color: '#1976d2' }}
                                    >
                                        {l.post_title} (#{l.post_id})
                                    </a>
                                </TableCell>
                                <TableCell align="center">{actionLabel[l.action] || l.action}</TableCell>
                                <TableCell align="center">{l.acted_by || '-'}</TableCell>
                                <TableCell align="center">
                                    {l.acted_at ? new Date(l.acted_at).toLocaleString('ko-KR') : '-'}
                                </TableCell>
                                <TableCell align="center">{l.memo || '-'}</TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ p: isMobile ? 1.5 : 0, width: '100%', minWidth: 0 }}>
            <HeaderBar />
            {isMobile ? <MobileList /> : <DesktopTable />}

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, val) => setPage(val)}
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        color="primary"
                        size={isMobile ? 'small' : 'medium'}
                    />
                </Box>
            )}
        </Box>
    );
}
